import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  useTheme,
  Button,
} from '@mui/material';
import { format } from 'date-fns';
import { LocalOfferOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NoDataInfo from './NoDataInfo';

interface Offer {
  offer_id: string;
  offer_name: string;
  start_date: string;
  end_date: string;
  status: 'running' | 'upcoming';
  scope: string;
}

interface OffersListProps {
  title: string;
  offers: Offer[];
  loading?: boolean;
  emptyMessage?: string;
  environment: 'staging' | 'prod';
}

export const headerStyles = {
  fontWeight: 600,
  textTransform: 'none',
  padding: '7px',
};

export const cellStyles = {
  fontSize: '12px',
  padding: '7px',
};

export const OffersList = ({
  title,
  offers = [],
  loading = false,
  emptyMessage = 'No offers available',
  environment,
}: OffersListProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { palette } = theme;
  const isLight = palette.mode === 'light';

  const handleViewAll = () => {
    const status = title.toLowerCase().includes('running') ? 'running' : 'upcoming';
    navigate(`/offers?sync_on=${environment}&status=${status}`);
  };
  if (loading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (offers.length === 0) {
    return (
      <NoDataInfo
        title={title}
        emptyMessage={emptyMessage}
        icon={<LocalOfferOutlined fontSize="small" color="primary" />}
      />
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferOutlined fontSize="small" color="primary" />
          {title}
        </Typography>
        <Button sx={{ p: 0 }} variant="text" size="small" onClick={handleViewAll}>
          View All
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 1,
          boxShadow: '2px 2px 5px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          minHeight: 178,
          border: isLight ? `1px solid ${palette.grey[200]}` : 'none',
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                background: isLight ? palette.primary.lighter : palette.primary.light,
              }}
            >
              <TableCell sx={headerStyles}>Offer Name</TableCell>
              <TableCell sx={headerStyles}>Platform</TableCell>
              <TableCell sx={headerStyles}>Start Date</TableCell>
              <TableCell sx={headerStyles}>End Date</TableCell>
              <TableCell sx={headerStyles}>Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {offers.map((offer) => (
              <TableRow key={offer.offer_id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={cellStyles}>{offer.offer_name}</TableCell>
                <TableCell sx={cellStyles}>{offer.scope}</TableCell>
                <TableCell sx={cellStyles}>
                  {offer.start_date ? format(new Date(offer.start_date), 'MMM dd, yyyy') : 'N/A'}
                </TableCell>
                <TableCell sx={cellStyles}>
                  {offer.end_date ? format(new Date(offer.end_date), 'MMM dd, yyyy') : 'N/A'}
                </TableCell>
                <TableCell sx={cellStyles}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1,
                      py: 0.3,
                      borderRadius: '4px',
                      bgcolor: offer.status === 'running' ? 'success.lighter' : 'warning.lighter',
                      color: offer.status === 'running' ? 'success.dark' : 'warning.dark',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    {offer.status === 'running' ? 'Running' : 'Upcoming'}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
